<template>
  <div class="landing-page">
    <!-- Header/Navigation -->
    <header class="landing-header">
      <div class="header-container">
        <div class="logo">
          <img src="images/zerocents-logo-small.jpeg" alt="ZeroCents" class="logo-img">
        </div>
        <nav class="nav-menu">
          <a href="#home" class="nav-link">Home</a>
          <a href="#features" class="nav-link">Features</a>
          <a href="#pricing" class="nav-link">Pricing</a>
          <a href="#faq" class="nav-link">FAQ</a>
          <a href="#about" class="nav-link">About</a>
          <a href="#contact" class="nav-link">Contact</a>
        </nav>
        <div class="header-actions">
          <a-button class="btn-login" @click="goToSignIn">Login</a-button>
          <a-button type="primary" class="btn-signup" @click="goToSignUp">Sign Up</a-button>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section id="home" class="hero-section">
      <div class="hero-container">
        <div class="hero-content">
          <h1 class="hero-title">Track Your Finances with Zero Hassle</h1>
          <p class="hero-subtitle">
            Simple, powerful, and free. Manage multiple organizations, track transactions,
            generate reports, and gain financial insights - all in one place.
          </p>
          <a-button type="primary" size="large" class="hero-cta" @click="goToSignUp">
            Get Started
          </a-button>
        </div>
        <div class="hero-image">
          <img src="images/zerocents-logo.png" alt="Money Tracker Dashboard">
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <div class="section-container">
        <h2 class="section-title">Powerful Features for Financial Success</h2>
        <p class="section-subtitle">Everything you need to manage your finances effectively</p>

        <a-row :gutter="[32, 32]" class="features-grid">
          <a-col :xs="24" :sm="12" :lg="8" v-for="feature in features" :key="feature.title">
            <div class="feature-card">
              <div class="feature-icon">
                <a-icon :type="feature.icon" />
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-description">{{ feature.description }}</p>
            </div>
          </a-col>
        </a-row>
      </div>
    </section>

    <!-- How It Works Section -->
    <section id="how-it-works" class="how-it-works-section">
      <div class="section-container">
        <h2 class="section-title">How It Works</h2>
        <p class="section-subtitle">Get started in minutes with our simple process</p>

        <div class="steps-container">
          <div class="step" v-for="(step, index) in steps" :key="index">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-content">
              <h3 class="step-title">{{ step.title }}</h3>
              <p class="step-description">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section">
      <div class="section-container">
        <h2 class="section-title">Simple, Transparent Pricing</h2>
        <p class="section-subtitle">Start tracking your finances for free</p>

        <div class="pricing-card">
<!--          <div class="pricing-badge">Free Forever</div>-->
          <h3 class="pricing-title">Free Plan</h3>
          <div class="pricing-price">
            <span class="price-currency">$</span>
            <span class="price-amount">0</span>
            <span class="price-period">/month</span>
          </div>
          <ul class="pricing-features">
            <li><a-icon type="check-circle" /> Unlimited organizations</li>
            <li><a-icon type="check-circle" /> Unlimited transactions</li>
            <li><a-icon type="check-circle" /> Category management</li>
            <li><a-icon type="check-circle" /> Financial reports</li>
            <li><a-icon type="check-circle" /> Multi-user support</li>
            <li><a-icon type="check-circle" /> Receipt uploads</li>
            <li><a-icon type="check-circle" /> Balance tracking</li>
            <li><a-icon type="check-circle" /> Data export</li>
          </ul>
          <a-button type="primary" size="large" block @click="goToSignUp">
            Get Started
          </a-button>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section id="faq" class="faq-section">
      <div class="section-container">
        <h2 class="section-title">Frequently Asked Questions</h2>
        <p class="section-subtitle">Everything you need to know about ZeroCents</p>

        <a-collapse accordion class="faq-collapse">
          <a-collapse-panel v-for="(faq, index) in faqs" :key="index" :header="faq.question">
            <p>{{ faq.answer }}</p>
          </a-collapse-panel>
        </a-collapse>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="about-section">
      <div class="section-container">
        <h2 class="section-title">About ZeroCents</h2>
        <div class="about-content">
          <div class="about-text">
            <h3 class="about-heading">Our Mission</h3>
            <p class="about-paragraph">
              At ZeroCents, we believe that managing your finances shouldn't be complicated
              or expensive. Our mission is to provide powerful financial tracking tools that
              are accessible to everyone, from individuals to small businesses and large organizations.
            </p>
            <p class="about-paragraph">
              Built with modern technology and designed with simplicity in mind, ZeroCents
              helps you gain insights into your financial health, make informed decisions,
              and achieve your financial goals.
            </p>
            <h3 class="about-heading">Why Choose Us?</h3>
            <ul class="about-list">
              <li><a-icon type="star" /> Free - no hidden costs</li>
              <li><a-icon type="star" /> Multi-organization support</li>
              <li><a-icon type="star" /> Secure and reliable</li>
              <li><a-icon type="star" /> Easy to use interface</li>
              <li><a-icon type="star" /> Regular updates and improvements</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="contact-section">
      <div class="section-container">
        <h2 class="section-title">Get In Touch</h2>
        <p class="section-subtitle">Have questions? We'd love to hear from you</p>

        <a-row :gutter="48" type="flex" align="top">
          <a-col :xs="24" :lg="12">
            <a-form :form="contactForm" @submit="handleContactSubmit" class="contact-form">
              <a-form-item label="Name" :colon="false">
                <a-input
                  v-decorator="['name', { rules: [{ required: true, message: 'Please enter your name' }] }]"
                  placeholder="Your name"
                  size="large"
                />
              </a-form-item>

              <a-form-item label="Email" :colon="false">
                <a-input
                  v-decorator="['email', {
                    rules: [
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]
                  }]"
                  placeholder="your.email@example.com"
                  size="large"
                />
              </a-form-item>

              <a-form-item label="Message" :colon="false">
                <a-textarea
                  v-decorator="['message', { rules: [{ required: true, message: 'Please enter your message' }] }]"
                  placeholder="Tell us how we can help..."
                  :rows="6"
                />
              </a-form-item>

              <a-form-item>
                <a-button type="primary" html-type="submit" size="large" block>
                  Send Message
                </a-button>
              </a-form-item>
            </a-form>
          </a-col>

          <a-col :xs="24" :lg="12">
            <div class="contact-info">
              <div class="contact-item">
                <a-icon type="mail" class="contact-icon" />
                <div>
                  <h4>Email</h4>
                  <p>support@zerocents.com</p>
                </div>
              </div>
<!--              <div class="contact-item">-->
<!--                <a-icon type="environment" class="contact-icon" />-->
<!--                <div>-->
<!--                  <h4>Address</h4>-->
<!--                  <p>123 Finance Street<br>San Francisco, CA 94105</p>-->
<!--                </div>-->
<!--              </div>-->
              <div class="contact-item">
                <a-icon type="clock-circle" class="contact-icon" />
                <div>
                  <h4>Support Hours</h4>
                  <p>Monday - Friday: 9am - 6pm PST<br>Weekend: Limited support</p>
                </div>
              </div>
            </div>
          </a-col>
        </a-row>
      </div>
    </section>

    <!-- Footer -->
    <footer class="landing-footer">
      <div class="footer-container">
        <div class="footer-top">
          <a-row :gutter="32">
            <a-col :xs="24" :sm="12" :lg="6">
              <div class="footer-section">
                <div class="footer-logo">
                  <img src="images/zerocents-logo.png" alt="ZeroCents" class="footer-logo-img">
                  <span class="footer-logo-text">ZeroCents</span>
                </div>
                <p class="footer-description">
                  Simple and powerful financial tracking for everyone.
                </p>
              </div>
            </a-col>

            <a-col :xs="24" :sm="12" :lg="6">
              <div class="footer-section">
                <h4 class="footer-heading">Quick Links</h4>
                <ul class="footer-links">
                  <li><a href="#home">Home</a></li>
                  <li><a href="#features">Features</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><a href="#faq">FAQ</a></li>
                </ul>
              </div>
            </a-col>

            <a-col :xs="24" :sm="12" :lg="6">
              <div class="footer-section">
                <h4 class="footer-heading">Company</h4>
                <ul class="footer-links">
                  <li><a href="#about">About</a></li>
                  <li><a href="#contact">Contact</a></li>
                  <li><router-link to="/privacy">Privacy Policy</router-link></li>
                  <li><router-link to="/terms">Terms of Service</router-link></li>
                </ul>
              </div>
            </a-col>

<!--            <a-col :xs="24" :sm="12" :lg="6">-->
<!--              <div class="footer-section">-->
<!--                <h4 class="footer-heading">Follow Us</h4>-->
<!--                <div class="social-links">-->
<!--                  <a href="#" class="social-link"><a-icon type="twitter" /></a>-->
<!--                  <a href="#" class="social-link"><a-icon type="facebook" /></a>-->
<!--                  <a href="#" class="social-link"><a-icon type="linkedin" /></a>-->
<!--                  <a href="#" class="social-link"><a-icon type="github" /></a>-->
<!--                </div>-->
<!--              </div>-->
<!--            </a-col>-->
          </a-row>
        </div>

<!--        <div class="footer-bottom">-->
<!--          <p>&copy; 2025 ZeroCents. All rights reserved.</p>-->
<!--        </div>-->
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Landing',
  data() {
    return {
      features: [
        {
          icon: 'fund',
          title: 'Multi-Organization Support',
          description: 'Manage finances for multiple organizations from a single account. Perfect for freelancers, small businesses, and families.'
        },
        {
          icon: 'transaction',
          title: 'Transaction Tracking',
          description: 'Record and categorize all your transactions with ease. Upload receipts and add detailed notes for better organization.'
        },
        {
          icon: 'bar-chart',
          title: 'Financial Reports',
          description: 'Generate comprehensive reports with visual charts. Track income, expenses, and net balance over any time period.'
        },
        {
          icon: 'folder',
          title: 'Category Management',
          description: 'Create custom categories for income and expenses. Organize your finances the way that makes sense for you.'
        },
        {
          icon: 'team',
          title: 'Team Collaboration',
          description: 'Invite team members with different roles. Admins, members, and viewers can work together seamlessly.'
        },
        {
          icon: 'lock',
          title: 'Secure & Private',
          description: 'Your financial data is encrypted and secure. We take privacy seriously and never share your information.'
        }
      ],
      steps: [
        {
          title: 'Create Your Account',
          description: 'Sign up in seconds with just your email. No credit card required.'
        },
        {
          title: 'Set Up Your Organization',
          description: 'Create an organization for your business, family, or personal finances.'
        },
        {
          title: 'Add Transactions',
          description: 'Start recording your income and expenses. Upload receipts and categorize transactions.'
        },
        {
          title: 'Track & Analyze',
          description: 'View reports, monitor your balance, and gain insights into your financial health.'
        }
      ],
      faqs: [
        {
          question: 'Is ZeroCents really free?',
          answer: 'Yes! ZeroCents is completely free to use with no hidden costs. We believe everyone should have access to quality financial tracking tools.'
        },
        {
          question: 'How many organizations can I create?',
          answer: 'You can create unlimited organizations. Whether you need one for personal use, your business, or multiple projects - we\'ve got you covered.'
        },
        {
          question: 'Can I invite team members?',
          answer: 'Absolutely! You can invite as many team members as you need. Assign them roles (Admin, Member, or Viewer) to control their access level.'
        },
        {
          question: 'Is my financial data secure?',
          answer: 'Security is our top priority. We use industry-standard encryption for all data transmission and storage. Your information is private and secure.'
        },
        {
          question: 'Can I export my data?',
          answer: 'Yes, you can export your transactions and reports in various formats. Your data is yours, and you can take it with you anytime.'
        },
        {
          question: 'What kind of reports can I generate?',
          answer: 'You can generate income statements, expense reports, balance tracking, and monthly overviews with visual charts. All reports are customizable by date range.'
        },
        {
          question: 'Do I need to download any software?',
          answer: 'No downloads required! ZeroCents is a web-based application that works in any modern browser. Access your finances from anywhere.'
        },
        {
          question: 'What if I need help or support?',
          answer: 'We\'re here to help! Contact us through the contact form or email support@zerocents.com. We typically respond within 24 hours.'
        }
      ]
    };
  },
  beforeCreate() {
    this.contactForm = this.$form.createForm(this, { name: 'contact' });
  },
  methods: {
    goToSignIn() {
      this.$router.push('/sign-in');
    },
    goToSignUp() {
      this.$router.push('/sign-up');
    },
    async handleContactSubmit(e) {
      e.preventDefault();
      this.contactForm.validateFields(async (err, values) => {
        if (!err) {
          try {
            const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
            const response = await fetch(`${apiBaseUrl}/api/contact/submit`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name: values.name,
                email: values.email,
                message: values.message,
              }),
            });

            if (!response.ok) {
              throw new Error('Failed to send message');
            }

            const data = await response.json();
            this.$message.success(data.message || 'Thank you for your message! We\'ll get back to you soon.');
            this.contactForm.resetFields();
          } catch (error) {
            console.error('Contact form error:', error);
            this.$message.error('Failed to send your message. Please try again or email us directly at support@zerocents.com');
          }
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.landing-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

// Header/Navigation
.landing-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-img {
  height: 40px;
  width: auto;
}

.logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1890ff;
}

.nav-menu {
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
}

.nav-link {
  color: #333;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;

  &:hover {
    color: #1890ff;
  }
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn-login {
  border-color: #1890ff;
  color: #1890ff;
}

// Hero Section
.hero-section {
  padding: 8rem 0 4rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  margin-top: 72px;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  gap: 4rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
}

.hero-content {
  flex: 1;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.hero-subtitle {
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;
}

.hero-cta {
  height: 50px;
  font-size: 1.125rem;
  padding: 0 2.5rem;
}

.hero-image {
  flex: 1;

  img {
    width: 100%;
    max-width: 500px;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
}

// Common Section Styles
.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.section-subtitle {
  font-size: 1.125rem;
  text-align: center;
  color: #666;
  margin-bottom: 3rem;
}

// Features Section
.features-section {
  padding: 5rem 0;
  background: #f9fafb;
}

.features-grid {
  margin-top: 3rem;
}

.feature-card {
  text-align: center;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

.feature-icon {
  font-size: 3rem;
  color: #1890ff;
  margin-bottom: 1.5rem;
}

.feature-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.feature-description {
  color: #666;
  line-height: 1.6;
}

// How It Works Section
.how-it-works-section {
  padding: 5rem 0;
}

.steps-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.step {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.step-number {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.step-description {
  color: #666;
  line-height: 1.6;
}

// Pricing Section
.pricing-section {
  padding: 5rem 0;
  background: #f9fafb;
}

.pricing-card {
  max-width: 400px;
  margin: 3rem auto;
  padding: 3rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
}

.pricing-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: #52c41a;
  color: #fff;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
}

.pricing-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.pricing-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-bottom: 2rem;
}

.price-currency {
  font-size: 2rem;
  font-weight: 600;
}

.price-amount {
  font-size: 4rem;
  font-weight: 700;
  color: #1890ff;
}

.price-period {
  font-size: 1.25rem;
  color: #666;
}

.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  text-align: left;

  li {
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;

    .anticon {
      color: #52c41a;
      margin-right: 0.75rem;
    }

    &:last-child {
      border-bottom: none;
    }
  }
}

// FAQ Section
.faq-section {
  padding: 5rem 0;
}

.faq-collapse {
  max-width: 800px;
  margin: 0 auto;
  background: transparent;
}

// About Section
.about-section {
  padding: 5rem 0;
  background: #f9fafb;
}

.about-content {
  max-width: 800px;
  margin: 3rem auto;
}

.about-text {
  line-height: 1.8;
}

.about-heading {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2rem 0 1rem;
  color: #1890ff;
}

.about-paragraph {
  margin-bottom: 1.5rem;
  color: #555;
}

.about-list {
  list-style: none;
  padding: 0;

  li {
    padding: 0.75rem 0;

    .anticon {
      color: #faad14;
      margin-right: 0.75rem;
    }
  }
}

// Contact Section
.contact-section {
  padding: 5rem 0;
}

.contact-form {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.contact-item {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.contact-icon {
  font-size: 2rem;
  color: #1890ff;
}

.contact-item h4 {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.contact-item p {
  color: #666;
  margin: 0;
  line-height: 1.6;
}

// Footer
.landing-footer {
  background: #001529;
  color: #fff;
  padding: 3rem 0 1rem;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.footer-top {
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;
}

.footer-section {
  h4 {
    margin-bottom: 1.5rem;
  }
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.footer-logo-img {
  height: 30px;
  width: auto;
}

.footer-logo-text {
  font-size: 1.25rem;
  font-weight: 700;
}

.footer-description {
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
}

.footer-heading {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 0.75rem;

    a {
      color: rgba(255, 255, 255, 0.65);
      text-decoration: none;
      transition: color 0.3s;

      &:hover {
        color: #fff;
      }
    }
  }
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-link {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-decoration: none;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  .anticon {
    font-size: 1.25rem;
  }
}

.footer-bottom {
  text-align: center;
  color: rgba(255, 255, 255, 0.45);

  p {
    margin: 0;
  }
}
</style>
